<script setup lang="ts">
// A new device opening a link created on one of the user's existing devices.
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { describeError } from '../api/http'
import type { LinkPreview } from '../api/types'
import { nameFromLink, transferFromLink } from '../lib/metacrypto'
import { useSession } from '../stores/session'
import Avatar from '../components/Avatar.vue'
import AuthFrame from '../components/AuthFrame.vue'

const route = useRoute()
const router = useRouter()
const session = useSession()
const token = computed(() => String(route.params.token ?? ''))
// The name chosen on the other device, in the fragment (never sent to the server).
const deviceName = computed(() => nameFromLink(route.hash))
const withHistory = computed(() => transferFromLink(route.hash) !== null)
const preview = ref<LinkPreview | null>(null)
const error = ref<string | null>(null)
const busy = ref(false)

onMounted(async () => {
  try {
    preview.value = await session.api.linkPreview(token.value)
  } catch (e) {
    error.value = describeError(e)
  }
})

async function link(): Promise<void> {
  busy.value = true
  error.value = null
  try {
    await session.linkDevice(token.value + route.hash)
    await router.replace({ name: 'home' })
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <AuthFrame testid="link-device" caption="device link">
      <template v-if="session.status === 'ready'">
        <h1 class="mb-2 text-2xl font-light">This browser is already signed in</h1>
        <p class="text-sm text-text-muted">Open the link on the device you want to add.</p>
        <button class="btn-ghost mt-4" @click="router.push('/')">Back to hrth</button>
      </template>
      <template v-else-if="preview">
        <Avatar :id="preview.profile.user_id" :name="preview.profile.display_name" :size="56" class="mb-4" />
        <h1 class="text-2xl font-light">Add this device to {{ preview.profile.display_name }}’s account?</h1>
        <p class="mono mt-2 text-text-muted">{{ preview.profile.user_id }}</p>
        <p v-if="deviceName" class="mt-4 text-sm text-text-secondary" data-testid="link-device-name">
          This device will be called <strong class="text-text-primary">{{ deviceName }}</strong>.
        </p>
        <p class="mt-4 text-sm text-text-muted">
          This device gets its own encryption keys and joins all your spaces and chats.
          <template v-if="withHistory">Then your other device sends it your message history, end-to-end encrypted.</template>
          <template v-else>Messages sent before now stay on your other devices.</template>
        </p>
        <p v-if="error" class="mt-3 border border-signal px-3 py-2 text-sm text-error">{{ error }}</p>
        <button class="btn-primary mt-5 w-full" :disabled="busy" data-testid="confirm-link" @click="link">
          {{ busy ? 'Setting up keys…' : 'Add this device' }}
        </button>
      </template>
      <template v-else-if="error">
        <h1 class="mb-2 text-2xl font-light">Link unavailable</h1>
        <p class="text-sm text-text-muted" data-testid="link-error">{{ error }}</p>
      </template>
      <p v-else class="mono text-text-muted">Checking link…</p>
  </AuthFrame>
</template>
